'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { RiErrorWarningLine, RiRefreshLine, RiWifiOffLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { connectionStatus, retryConnection, subscribeConnection } from '@/lib/connection';
import { dur, spring, springFade } from '@/lib/motion';

/**
 * Hata sınırı — ve bu dosya bir dönem HİÇ YOKTU.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Kâğıttaki ekran
 * ═══════════════════════════════════════════════════════════════════════════
 * Kod tabanının birçok yerinde "hata sınırı 'bağlanılamadı' ekranını bassın"
 * yazıyordu — `server-session.ts`, `backstage-server.ts`, sayfa sayfa hata
 * yolları. Öyle bir ekran yoktu: `app/` altında tek bir `error.tsx` bile
 * bulunmuyordu. Sunucu bileşeninde fırlayan her hata, Next'in çıplak
 * "Application error" sayfasına düşüyordu. Yani yorumlar bir sözleşmeden
 * bahsediyor, kullanıcı ham bir 500 görüyordu.
 *
 * ---------------------------------------------------------------------------
 * Neden iki ayrı cümle
 * ---------------------------------------------------------------------------
 * "Bir şeyler ters gitti" doğru ama işe yaramaz bir cümle — kullanıcının
 * yapabileceği bir şey söylemiyor. Hata çoğu zaman İNTERNETİN kopmasından
 * geliyor ve o durumda doğru cümle bambaşka: "bağlantınız yok, geri
 * geldiğinde devam edeceksiniz". Bu yüzden ekran bağlantı durumunu okuyup
 * hangi cümleyi kuracağına karar veriyor.
 *
 * `error.digest` yazıyla gösterilmiyor ama konsola düşüyor: kullanıcıya
 * rastgele bir kimlik göstermek güven vermiyor, destek istendiğinde ise
 * sunucu logundaki satırla eşleştiren tek şey o.
 */
export default function LocaleError({
  error,
  retry,
}: {
  error: Error & { digest?: string };
  retry: () => void;
}) {
  const t = useTranslations('connection');
  const reduce = useReducedMotion();
  const status = useSyncExternalStore(subscribeConnection, connectionStatus, () => 'online');
  const offline = status === 'offline';

  useEffect(() => {
    console.error('[mentorivo] ekran hatası', error.digest ?? '', error);
  }, [error]);

  async function onRetry() {
    // Kopukluk şüphesi varsa önce hattı yokluyoruz: sunucuya ulaşılamazken
    // yeniden denemek aynı hatayı bir kez daha üretmekten başka bir şey
    // yapmıyor ve kullanıcı "düğme bozuk" diye okuyor.
    if (offline) await retryConnection();
    retry();
  }

  return (
    <main className="conn-page">
      <motion.div
        className="conn-card"
        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={springFade(spring.default, dur.fast)}
      >
        <span className="conn-icon" aria-hidden="true">
          {offline ? <RiWifiOffLine size={24} /> : <RiErrorWarningLine size={24} />}
        </span>

        <h1 className="conn-title">{offline ? t('offlineTitle') : t('errorTitle')}</h1>
        <p className="conn-body">{offline ? t('offlineBody') : t('errorBody')}</p>

        <button type="button" className="conn-retry" onClick={onRetry}>
          <RiRefreshLine size={16} aria-hidden="true" />
          {offline ? t('retry') : t('errorRetry')}
        </button>
      </motion.div>
    </main>
  );
}
